import { Component, inject, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { MerchantSummary } from '../../core/models';
import { LocationUsersPanel } from './location-users-panel';

// Every location user, across merchants. The same panel the merchant page carries under its Users tab —
// here with a merchant filter instead of a fixed merchant.
@Component({
    selector: 'app-location-users',
    standalone: true,
    imports: [FormsModule, CardModule, MessageModule, SelectModule, TooltipModule, LocationUsersPanel],
    template: `
        <p-card>
            <ng-template pTemplate="title"><span>Location users</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Sign-ins for store staff. Administrators are managed under Administration → Users &amp; Roles." tooltipPosition="top"></i></ng-template>

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="merchants()" optionLabel="name" optionValue="merchantId"
                          [ngModel]="merchantId()" (ngModelChange)="merchantId.set($event ?? null)"
                          placeholder="All merchants" [showClear]="true" [filter]="true" styleClass="w-full sm:w-64" />
            </div>

            @if (merchantsError()) {
                <p-message severity="warn" styleClass="mb-3">{{ merchantsError() }}</p-message>
            }

            <app-location-users-panel [merchantId]="merchantId()" />
        </p-card>
    `
})
export class LocationUsers implements OnInit {
    private readonly api = inject(MiddlewareApi);

    readonly merchantId = signal<string | null>(null);
    readonly merchants = signal<MerchantSummary[]>([]);
    readonly merchantsError = signal<string | null>(null);

    ngOnInit(): void {
        this.api.getMerchants().subscribe({
            next: (m) => this.merchants.set(m ?? []),
            error: (e) => this.merchantsError.set(this.describe(e))
        });
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.error?.error) return e.error.error;
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
